import type { ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RouteGuard, ToastProvider, hasScope, useSession, type Session } from '@aramo/fe-foundation';

import { LoginPage, PlatformSignedOut } from './LoginPage';
import { PlatformShell } from './shell/PlatformShell';
import { PlatformAccessDenied } from './shell/PlatformAccessDenied';
import { PLATFORM_PERMISSIONS as P } from './components/permissions';
import { DashboardView } from './dashboard/DashboardView';
import { TenantsListView } from './tenants/TenantsListView';
import { TenantDetailView } from './tenants/TenantDetailView';
import { ProvisionTenantView } from './tenants/ProvisionTenantView';
import { SkillsRegistryView } from './skills/SkillsRegistryView';
import { SkillDetailView } from './skills/SkillDetailView';
import { ReviewQueueView } from './skills/ReviewQueueView';
import { ProposalsListView } from './skills/ProposalsListView';
import { ProposalDetailView } from './skills/ProposalDetailView';
import { DocumentTemplatesListView } from './document-templates/DocumentTemplatesListView';
import { DocumentTemplateDetailView } from './document-templates/DocumentTemplateDetailView';
import { TemplateVariablesView } from './document-templates/TemplateVariablesView';
import { DocumentTypesView } from './document-types/DocumentTypesView';
import { MatchingPolicyView } from './matching/MatchingPolicyView';
import { PersonalReviewView } from './personal/PersonalReviewView';
import { PersonalInviteView } from './personal/PersonalInviteView';
import { SignAbuseReportsView } from './personal/SignAbuseReportsView';
import { PlatformAdminsView } from './admins/PlatformAdminsView';
import { COMMERCIAL_PERMISSIONS as CP } from './components/permissions';
import { ApprovalsInboxView } from './commercial/ApprovalsInboxView';
import { CommercialAccessDenied } from './commercial/CommercialAccessDenied';
import { SubscriptionsView } from './commercial/subscriptions/SubscriptionsView';
import { CatalogView } from './commercial/catalog/CatalogView';
import { PolicyView } from './commercial-governance/PolicyView';
import { AuditExplorerView } from './commercial-governance/AuditExplorerView';
import { ContractsView } from './contracts/ContractsView';
import { CONTRACT_READ_SCOPE } from './contracts/contracts-api';
import { SupportExtensionsView } from './commercial/support-extensions/SupportExtensionsView';
import { RenewalsView } from './commercial/finance/RenewalsView';
import { OutstandingPaymentsView } from './commercial/finance/OutstandingPaymentsView';
import { BillingReconciliationView } from './commercial/finance/BillingReconciliationView';
import { RevenueView } from './commercial/finance/RevenueView';
import { ConsumptionView } from './commercial/usage/ConsumptionView';
import { AlertsView } from './commercial/alerts/AlertsView';

// The platform console app. RouteGuard handles only loading, unauthenticated
// (→ sign-in) and the TENANT_ACCESS_DENIED wrong-account redirect — it no longer
// receives `requireScope` (PLATFORM-UX-1 directive R-5). Every permission gate —
// the console entry (platform:tenant:read) and each route's own scope — renders
// the in-shell AccessDenied (handoff §3.5) INSIDE PlatformShell, with the
// signed-in identity and the account-switch / sign-out actions, and never
// redirects. Server-side enforcement stays authoritative.
export function App() {
  const state = useSession();
  const location = useLocation();

  // Inc-3 PR-3.5 (Workstream B) — /login is the SESSION-LESS landing the
  // auth-service callback navigates to on a login failure (?error=<CODE>). It
  // must render OUTSIDE RouteGuard (an unauthenticated user is exactly who lands
  // here) — otherwise RouteGuard would bounce them straight back to the IdP and
  // swallow the error. Short-circuit before the guard; same param/pattern as
  // ats-web.
  if (location.pathname === '/login') {
    return (
      <ToastProvider>
        <LoginPage />
      </ToastProvider>
    );
  }

  // T2-E1-HF3 — the PUBLIC signed-out landing. Same short-circuit-before-guard
  // pattern as /login: the Cognito logout returns the browser here
  // (deriveSignoutRedirect → /signed-out); it must render session-less and NOT
  // re-enter RouteGuard (which would silently re-authenticate via SSO).
  if (location.pathname === '/signed-out') {
    return (
      <ToastProvider>
        <PlatformSignedOut />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <RouteGuard sessionStateOverride={state}>
        {state.status === 'authenticated' ? <PlatformConsole session={state.session} /> : null}
      </RouteGuard>
    </ToastProvider>
  );
}

/** The authenticated console: the shell, the entry gate and every routed screen. */
export function PlatformConsole({ session }: { readonly session: Session }) {
  // Entry gate (handoff §2): without platform:tenant:read every route shows the
  // in-shell access-denied state for the whole console.
  if (!hasScope(session, P.tenantRead)) {
    return (
      <PlatformShell session={session}>
        <PlatformAccessDenied session={session} scope={P.tenantRead} />
      </PlatformShell>
    );
  }

  // A per-route gate: the screen, or the in-shell access-denied state naming the
  // page and its missing permission (with "Back to dashboard").
  const gate = (scope: string, page: string, element: ReactElement): ReactElement =>
    hasScope(session, scope) ? element : <PlatformAccessDenied session={session} scope={scope} page={page} />;

  // CCP-B1 — a Commercial route's gate: the screen, or the in-shell Commercial
  // access-denied state naming the six commercial roles (CCP-UI v1.0 handoff §2;
  // super_admin holds no commercial scope, so every Commercial deep link lands here).
  // CCP-UI-V12 (AM-1, AM-2): a Finance, Usage or Operations page names itself on the card, and a
  // scope-gated page names the scope it needs and its five holder roles.
  const commercialGate = (scope: string, page: string, element: ReactElement, group?: 'Finance' | 'Usage' | 'Operations'): ReactElement =>
    hasScope(session, scope) ? element : <CommercialAccessDenied session={session} scope={scope} page={page} group={group} />;

  return (
    <PlatformShell session={session}>
      <Routes>
        {/* Inc-3 PR-3.8 — the operator dashboard is the default post-login
            screen; the tenant list is one click away in the rail. */}
        <Route path="/" element={<DashboardView session={session} />} />
        <Route path="/tenants" element={<TenantsListView session={session} />} />
        <Route path="/tenants/new" element={<ProvisionTenantView session={session} />} />
        {/* The detail view keeps reading its tab from `?tab=` (directive R-12). */}
        <Route path="/tenants/:id" element={<TenantDetailView session={session} />} />
        {/* SKILL-TAX-1F-C1/C2 — the platform skill governance console, gated on
            platform:skill:read; manage controls gate on platform:skill:manage
            inside the views. Static paths precede /skills/:id so react-router
            ranks them ahead of the dynamic detail route. */}
        <Route
          path="/skills"
          element={gate(P.skillRead, 'Registry', <SkillsRegistryView session={session} />)}
        />
        <Route
          path="/skills/review-queue"
          element={gate(P.skillRead, 'Review queue', <ReviewQueueView session={session} />)}
        />
        <Route path="/skills/proposals" element={gate(P.skillRead, 'Proposals', <ProposalsListView />)} />
        <Route
          path="/skills/proposals/:id"
          element={gate(P.skillRead, 'Proposals', <ProposalDetailView session={session} />)}
        />
        <Route path="/skills/:id" element={gate(P.skillRead, 'Registry', <SkillDetailView session={session} />)} />
        {/* DOC-TEMPLATE-LIB-1 T-2 (§8.1) — Aramo default document templates + the
            template variable catalog, gated on platform:document_template:read;
            write controls gate on platform:document_template:manage inside. */}
        <Route
          path="/document-templates"
          element={gate(P.documentTemplateRead, 'Document templates', <DocumentTemplatesListView session={session} />)}
        />
        <Route
          path="/document-templates/:id"
          element={gate(P.documentTemplateRead, 'Document templates', <DocumentTemplateDetailView session={session} />)}
        />
        {/* TEMPLATE-PRODUCT-1 §4.4 — Platform Admin document types (read gate; writes
            gate on platform:document_template:manage inside). */}
        <Route path="/document-types" element={gate(P.documentTemplateRead, 'Document types', <DocumentTypesView session={session} />)} />
        <Route
          path="/template-variables"
          element={gate(P.documentTemplateRead, 'Template variables', <TemplateVariablesView session={session} />)}
        />
        {/* ATI-SEP-1 A2 A-c (P1) — the platform-owned matching calibration, gated
            on platform:matching_policy:read; new-version + activate gate on
            platform:matching_policy:write inside the view. */}
        <Route
          path="/matching"
          element={gate(P.matchingPolicyRead, 'Matching policy', <MatchingPolicyView session={session} />)}
        />
        {/* Aramo Sign Personal (Free) §6.5/§10 — the review queue and pilot
            invites; the API guards them with platform:tenant:lifecycle:manage
            (authoritative, amendment 3), mirrored here. */}
        <Route
          path="/personal-review"
          element={gate(P.tenantLifecycleManage, 'Review queue', <PersonalReviewView />)}
        />
        <Route
          path="/personal-invites"
          element={gate(P.tenantLifecycleManage, 'Pilot invites', <PersonalInviteView />)}
        />
        {/* M-A7 §5.2 — the abuse-report queue across every Sign sender (same scope as the API). */}
        <Route
          path="/sign-abuse-reports"
          element={gate(P.tenantLifecycleManage, 'Abuse reports', <SignAbuseReportsView />)}
        />
        {/* PLATFORM-UX-1 (directive R-12) — Platform admins at /admins (never under
            /platform/, which nginx proxies to the backend). */}
        {/* CCP-B5 (handoff §5.8) — Platform admins also opens for the commercial roles
            (their commercial-roles view); super_admin keeps the invite view. */}
        <Route
          path="/admins"
          element={
            hasScope(session, P.adminInvite) || hasScope(session, CP.approvalRead) ? (
              <PlatformAdminsView session={session} />
            ) : (
              <PlatformAccessDenied session={session} scope={P.adminInvite} page="Platform admins" />
            )
          }
        />
        {/* ── CCP-B1 — the Commercial group (handoff §2, directive R-12: never under
            /platform/). Approvals is B1's, Catalog B2's, Policy and Audit B5's, Contracts B4's, Subscriptions B3's. */}
        <Route path="/commercial" element={<Navigate to="/commercial/approvals" replace />} />
        <Route
          path="/commercial/approvals"
          element={commercialGate(CP.approvalRead, 'approvals', <ApprovalsInboxView session={session} />)}
        />
        {/* CCP-B3 (PBD §5.3 row B3) — the Subscriptions workbench (handoff §5.3), read-only; a row
            opens Tenant › Commercial, where every action is a change request (replaces B1's placeholder). */}
        <Route path="/commercial/subscriptions" element={commercialGate(CP.subscriptionRead, 'subscriptions', <SubscriptionsView />)} />
        {/* CCP-B2 (PBD §5.3 row B2) — the product and plan catalog; draft actions gate on
            platform:catalog:draft inside the view (merge train: replaces B1's placeholder). */}
        <Route path="/commercial/catalog" element={commercialGate(CP.catalogRead, 'catalog', <CatalogView session={session} />)} />
        {/* CCP-B4 — Commercial › Contracts (handoff §5.5), gated on platform:subscription:read
            (CCP A1 §4.1; catalog_manager and super_admin get the Commercial card, TD-B4-3). */}
        <Route path="/commercial/contracts" element={commercialGate(CONTRACT_READ_SCOPE, 'contracts', <ContractsView session={session} />)} />
        {/* CCP-B5 (handoff §5.6, §5.7; B5-P) — Commercial › Policy and Commercial › Audit, behind
            the Commercial access-denied card (merge train: replaces B1's placeholders). */}
        <Route path="/commercial/policy" element={commercialGate(CP.approvalRead, 'policy', <PolicyView />)} />
        <Route path="/commercial/audit" element={commercialGate(CP.commercialAuditRead, 'audit', <AuditExplorerView />)} />
        {/* CCP-B6 — Commercial › Support extensions (extensions and escalations), gated on the list
            route's scope platform:subscription:read, behind B1's commercial card (merge train TD-MERGE-32). */}
        <Route path="/commercial/support-extensions" element={commercialGate(CP.subscriptionRead, 'support extensions', <SupportExtensionsView session={session} />)} />
        {/* CCP-UI-V12 (CCP-UI v1.1 §12.1, §13; v1.2 §17) — Finance, Usage and Operations › Alerts,
            behind B1's commercial card with their read scope (AM-1, AM-2). Every screen is a read. */}
        <Route path="/commercial/finance" element={<Navigate to="/commercial/finance/renewals" replace />} />
        <Route path="/commercial/finance/renewals" element={commercialGate(CP.billingRead, 'renewals', <RenewalsView />, 'Finance')} />
        <Route path="/commercial/finance/outstanding-payments" element={commercialGate(CP.billingRead, 'outstanding payments', <OutstandingPaymentsView session={session} />, 'Finance')} />
        <Route path="/commercial/finance/reconciliation" element={commercialGate(CP.billingRead, 'billing reconciliation', <BillingReconciliationView />, 'Finance')} />
        <Route path="/commercial/finance/revenue" element={commercialGate(CP.billingRead, 'revenue', <RevenueView session={session} />, 'Finance')} />
        <Route path="/commercial/usage" element={commercialGate(CP.subscriptionRead, 'consumption', <ConsumptionView />, 'Usage')} />
        <Route path="/commercial/alerts" element={commercialGate(CP.billingRead, 'alerts', <AlertsView />, 'Operations')} />
        {/* ── end CCP-B1 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PlatformShell>
  );
}
